import React from 'react';

import type { IRadioProps } from './Radio.types';

export const Radio: React.FC<IRadioProps> = ({
  label,
  description,
  size = 'md',
  error = false,
  containerClassName = '',
  labelClassName = '',
  disabled = false,
  id,
  className = '',
  ...radioProps
}: IRadioProps) => {

  // Size mapping for radio
  const sizeMapper = {
    'sm': {
      radio: 'w-4 h-4',
      text: 'text-bg-v2-sm',
      gap: 'gap-bg-v2-md',
    },
    'md': {
      radio: 'w-5 h-5',
      text: 'text-bg-v2-md',
      gap: 'gap-bg-v2-md',
    },
    'lg': {
      radio: 'w-6 h-6',
      text: 'text-bg-v2-lg',
      gap: 'gap-bg-v2-md',
    },
  };

  // Generate unique ID if not provided
  const radioId = id || `radio-${Math.random().toString(36).substr(2, 9)}`;

  // Build radio classes
  const radioClasses = `
    appearance-none
    ${sizeMapper[size].radio}
    rounded-full
    border
    ${error ? 'border-border-error' : 'border-border-primary'}
    ${error ? 'text-text-error-primary' : 'text-text-brand-primary'}
    bg-bg-primary
    checked:border-[5px]
    checked:border-bg-brand-solid
    focus:outline-none
    focus-visible:ring-2
    focus-visible:ring-offset-1
    ${error ? 'focus-visible:ring-focus-ring-error' : 'focus-visible:ring-focus-ring'}
    disabled:bg-bg-disabled
    disabled:border-border-disabled
    disabled:cursor-not-allowed
    cursor-pointer
    transition-all
    duration-200
    ${className}
  `.trim().replace(/\s+/g, ' ');

  return (
    <div className={`flex flex-col ${sizeMapper[size].gap} ${containerClassName}`}>
      <div className={`flex items-start ${sizeMapper[size].gap}`}>
        <div className="flex items-center h-5">
          <input
            type="radio"
            id={radioId}
            disabled={disabled}
            className={radioClasses}
            {...radioProps}
          />
        </div>
        
        {label && (
          <div className="flex flex-col gap-bg-v2-xxs flex-1">
            <label
              htmlFor={radioId}
              className={`
                ${sizeMapper[size].text}
                font-bg-v2-medium
                ${error ? 'text-text-error-primary' : 'text-text-primary'}
                ${disabled ? 'text-text-disabled cursor-not-allowed' : 'cursor-pointer'}
                ${labelClassName}
              `.trim().replace(/\s+/g, ' ')}
            >
              {label}
            </label>
            
            {description && (
              <span className={`text-bg-v2-sm text-text-tertiary ${disabled ? 'text-text-disabled' : ''}`}>
                {description}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

